import React, { memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import cx from "classnames";
import styles from "./List.module.less";
import { Spin } from "antd";
import { initTracking } from "@Lib/jotai/tracking";
import { useAtom } from "jotai";

interface IProps {
  brandsListItem: {
    list: IListData[];
    index: IAlphabetData[];
  };
}

export interface IListData {
  country: string;
  id: number;
  kindNum: number;
  label: string;
  link: string;
  name: string;
  urlName: string;
}

interface IAlphabetData {
  hasKind: number;
  label: string;
}

const arrNav = [
  { label: "全部", value: "all" },
  { label: "日系", value: "日系" },
  { label: "德系", value: "德系" },
  { label: "英系", value: "英系" },
  { label: "其他", value: "其他" },
];

const List = React.memo((props: IProps) => {
  const { brandsListItem } = props;
  const [listData, setListData] = useState<IListData[]>([]);
  const [alphabetData, setAlphabetData] = useState<IAlphabetData[]>([]);
  const [newListData, setNewListData] = useState<IListData[]>([]);
  const [listActive, setListActive] = useState<number>();
  const [alphabetActive, setAlphabetActive] = useState<string>();
  const [loading, setLoading] = useState(false);
  const themeColorStyle = useRef<any>({});
  const [tracking] = useAtom(initTracking);

  const listType = useRef<string>("all");
  const ref = useRef<any>([]);
  const targetRef = useRef<HTMLUListElement | any>(null);

  const getRef = (dom) => {
    const findIndex = ref.current.findIndex((res) => res === dom);
    if (findIndex === -1 && dom) {
      ref.current.push(dom);
    }
  };

  const getBrandsList = async () => {
    setListData(brandsListItem.list);
    setAlphabetData(brandsListItem.index);
    setBrandsList(brandsListItem.list);
  };

  const setBrandsList = async (data) => {
    const list = data || listData;
    let newList: any[] = [];
    list.find((item, index) => {
      if (listType.current === "all") {
        const findIndex = newList.length > 0 && newList.findIndex((res) => res === item.label);
        if (findIndex === -1 || !newList.length) {
          newList.push(item.label);
        }
      } else {
        newList = [listType.current];
      }
    });
    const value = listType.current === "all" ? newList.sort() : newList;
    setNewListData(value);
    setBrandsKey(value, list);
  };

  const setBrandsKey = async (alphabetList, brandsList) => {
    if (window.BRAND_INFO_DESCRIPTION && brandsList.length > 0) {
      const brandId = Number(window.BRAND_INFO_DESCRIPTION.brandId);
      setListActive(brandId);

      const index = brandsList.findIndex((brand) => brand.id === brandId);
      if (index >= 0) {
        handleClickAlphabet(
          {
            label: brandsList[index].label,
          },
          alphabetList,
        );
      }
    }
  };

  const handleClickLink = async (res: IListData) => {
    setListActive(res.id);
    const str: any = location.pathname.split("/").find((res) => res.includes("x-"));
    let link = "";
    if (str && str !== "") {
      link = `${location.origin}/Models/${res.urlName}/${window.SEARCH_TYPE === "default" ? "" : "ev/"}search/${str}`;
    } else {
      link = `${location.origin}/Models/${!str ? `${res.urlName}` : ""}${window.SEARCH_TYPE === "default" ? "" : "/ev"
        }`;
    }

    tracking && tracking.trackEvent({
      name: "element_clicked",
      params: {
        "page_sub_business": "新車資料庫",
        "page_business": "新車",
        "trigger_id": "4044",
        "page_id": "73",
        "trigger_name": "找汽車頁廠牌按首字母搜尋",
        "object_type": "brand_id",
        "next_spm": "3.73.677.2135",
        "page": "找車頻道頁",
        "channel": "新車",
        "element_id": "找汽車頁廠牌按首字母搜尋",
        "element": "按鈕",
        "module": "廠牌字母搜尋",
        "tracker_method": "config",
        object_id: res.id
      }
    });

    window.location.href = link;
  };

  const handleClickNav = async (item) => {
    setNewListData([]);
    ref.current = [];
    listType.current = item.value;
    item.value === "all" && setAlphabetActive("");
    setBrandsList(listData);
  };

  const handleClickAlphabet = async (item, alphabetList = []) => {
    if (listType.current !== "all") return;
    const list = newListData.length ? newListData : alphabetList;
    setAlphabetActive(item.label);
    const index: any = list.length > 0 && list.findIndex((res) => res === item.label);
    const dom: HTMLParagraphElement = ref.current[index];
    if (dom) {
      const top = dom.offsetTop - 38;
      scrollTo(top);
    }
  };

  const scrollTo = useCallback((top) => {
    targetRef.current!.scrollTo(0, top);
  }, []);

  const handleChangeNavValue = async (url): Promise<any> => {
    window.location.href = url;
  };

  /**
   * 切换主题色
   */
  const getThemeColorStyle = async (): Promise<any> => {
    themeColorStyle.current = {
      color: "#fff",
      background: window.SEARCH_TYPE ? (window.SEARCH_TYPE === "default" ? "#3264ff" : "#1DBF76") : "#3264ff",
    };
  };

  useEffect(() => {
    getBrandsList();
    getThemeColorStyle();

    tracking && tracking.trackEvent({
      name: "element_exposured",
      params: {
        "page_sub_business": "新車資料庫",
        "page_business": "新車",
        "trigger_id": "4043",
        "page_id": "73",
        "trigger_name": "找汽車頁廠牌按首字母搜尋",
        "next_spm": "3.73.677.2135",
        "page": "找車頻道頁",
        "channel": "新車",
        "element_id": "找汽車頁廠牌按首字母搜尋",
        "element": "按鈕",
        "module": "廠牌字母搜尋",
        "tracker_method": "config"
      }
    });
  }, []);

  return (
    <div className={styles["brands-list"]}>
      <Spin spinning={loading}>
        <ul className={styles["nav"]}>
          <li
            className={cx(styles["item"], { [styles["active"]]: window.SEARCH_TYPE === "default" })}
            onClick={() => handleChangeNavValue(`/Models`)}
            style={{ backgroundColor: window.SEARCH_TYPE === "default" ? "#3264FF" : "#F5F5F5" }}
          >
            找新車
          </li>
          <li
            className={cx(styles["item"], { [styles["active"]]: window.SEARCH_TYPE === "ev" })}
            onClick={() => handleChangeNavValue(`/Models/ev`)}
            style={{ backgroundColor: window.SEARCH_TYPE === "ev" ? "#1DBF76" : "#F5F5F5" }}
          >
            電動車
          </li>
        </ul>
        <div className={styles["list-item"]} ref={targetRef}>
          <div className={styles["list-series"]}>
            {arrNav.map((item) => (
              <span key={item.value} className={styles["series-span"]} onClick={() => handleClickNav(item)}>
                {item.label}
              </span>
            ))}
          </div>
          {listType.current === "all" && (
            <div className={styles["alphabet"]}>
              {alphabetData.length > 0 &&
                alphabetData.map((item, index) => (
                  <p
                    key={index}
                    className={cx(styles["str"], { [styles["active"]]: !item.hasKind })}
                    onClick={() => item.hasKind && handleClickAlphabet(item)}
                    style={{
                      backgroundColor: alphabetActive !== item.label ? "" : themeColorStyle.current.background,
                      color: !item.hasKind ? "#E8E8E8" : alphabetActive !== item.label ? "rgba(0, 0, 0, 0.85)" : "#fff",
                    }}
                  >
                    {item.label}
                  </p>
                ))}
            </div>
          )}
          <div>
            {newListData.map((item, index) => (
              <div className={cx(styles["list-box"])} key={index} ref={getRef} id={"list-item" + item}>
                <p className={styles["box-title"]}>{item}</p>
                <div className={styles["box-item"]}>
                  {listData.map((res: any, inx) => (
                    <>
                      {(listType.current === "all" ? res.label === item : res.country === item) && (
                        <div
                          key={inx}
                          className={cx(styles["item-text"])}
                          onClick={() => handleClickLink(res)}
                          style={{ color: listActive !== res.id ? "#333333" : themeColorStyle.current.background }}
                        >
                          {res.name}({res.kindNum || 0})
                        </div>
                      )}
                    </>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Spin>
    </div>
  );
});

export default memo(List);
